import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { axiosInstance } from '../helpers'
import { normalizeListResponse } from '../helpers/companyDocumentList'
import { OrganizationDocumentType } from '../models/Company/types'

const QUERY_ROOT = ['organizationDocumentTypes'] as const

export interface OrgDocumentTypeListParams {
    organization: number | null
    includeInactive?: boolean
}

export interface CreateOrganizationDocumentTypeInput {
    organization: number
    name: string
    description?: string | null
    required_company_ids?: number[]
    is_active?: boolean
}

export interface UpdateOrganizationDocumentTypeInput {
    name?: string
    description?: string | null
    required_company_ids?: number[]
    is_active?: boolean
}

const buildListQueryString = (params: OrgDocumentTypeListParams) => {
    if (!params.organization) return ''
    const searchParams = new URLSearchParams()
    searchParams.append('organization', String(params.organization))
    if (params.includeInactive) {
        searchParams.append('include_inactive', 'true')
    }
    const qs = searchParams.toString()
    return qs ? `?${qs}` : ''
}

export const useOrganizationDocumentTypes = (
    params: OrgDocumentTypeListParams,
) => {
    const queryClient = useQueryClient()
    const queryString = buildListQueryString(params)

    const { data: types, isLoading, error, refetch } = useQuery<
        OrganizationDocumentType[],
        Error
    >({
        queryKey: [...QUERY_ROOT, params.organization, params.includeInactive],
        queryFn: async () => {
            const res = await axiosInstance.get<unknown>(
                `/company/organization_document_type/${queryString}`,
            )
            return normalizeListResponse<OrganizationDocumentType>(res.data)
        },
        enabled: !!params.organization,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    const createType = useMutation({
        mutationFn: (body: CreateOrganizationDocumentTypeInput) =>
            axiosInstance.post<OrganizationDocumentType>(
                '/company/organization_document_type/',
                body,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [...QUERY_ROOT] })
            toast.success('Document type created')
        },
        onError: (error: any) => {
            const msg =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to create document type'
            toast.error(msg)
        },
    })

    const updateType = useMutation({
        mutationFn: ({
            id,
            body,
        }: {
            id: number
            body: UpdateOrganizationDocumentTypeInput
        }) =>
            axiosInstance.patch<OrganizationDocumentType>(
                `/company/organization_document_type/${id}/`,
                body,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [...QUERY_ROOT] })
            toast.success('Document type updated')
        },
        onError: (error: any) => {
            const msg =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to update document type'
            toast.error(msg)
        },
    })

    const deleteType = useMutation({
        mutationFn: (id: number) =>
            axiosInstance.delete(`/company/organization_document_type/${id}/`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [...QUERY_ROOT] })
            toast.success('Document type deleted')
        },
        onError: (error: any) => {
            const msg =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to delete document type'
            toast.error(msg)
        },
    })

    const sortedTypes = [...(types ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name),
    )

    return {
        documentTypes: sortedTypes,
        isLoading,
        error,
        refetch,
        createType,
        updateType,
        deleteType,
        isCreating: createType.isLoading,
        isUpdating: updateType.isLoading,
        isDeleting: deleteType.isLoading,
    }
}
